import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, ArrowUpRightSquare, Lock, LockOpen, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { useSession } from '@/hooks/useSession';
import {
  deleteArtifact,
  patchArtifact,
  supersedeArtifact,
  type Artifact,
  type ArtifactStatus,
  type ArtifactVisibility,
  type PatchArtifactBody,
  type UnarchiveRestoreStatus,
} from '@/lib/api';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { LinkedArtifactPicker } from './LinkedArtifactPicker';
import { OwnerTransferControl } from './OwnerTransferControl';

interface ArtifactLifecyclePanelProps {
  artifact: Artifact;
}

/**
 * Restore targets per Adjustments Log #7 round-3: only `draft` or `review`.
 * The schema (`unarchiveRestoreStatusSchema`) also enforces this server-side.
 * For ADR / note (no `review` status), only `draft` is offered.
 */
function restoreTargetsForType(t: Artifact['type']): UnarchiveRestoreStatus[] {
  if (t === 'adr' || t === 'note') return ['draft'];
  return ['draft', 'review'];
}

export function ArtifactLifecyclePanel({
  artifact,
}: ArtifactLifecyclePanelProps): React.ReactElement {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user, isAdmin } = useSession();
  const isOwnerOrAdmin = isAdmin || (user !== null && user.id === artifact.ownerId);

  const [statusError, setStatusError] = useState<string | null>(null);
  const [visibilityError, setVisibilityError] = useState<string | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [restoreStatus, setRestoreStatus] = useState<UnarchiveRestoreStatus>('draft');
  const [supersedeOpen, setSupersedeOpen] = useState(false);
  const [supersedeError, setSupersedeError] = useState<string | null>(null);

  const patchMutation = useMutation({
    mutationFn: (body: PatchArtifactBody) => patchArtifact(artifact.id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'snapshots'] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      void queryClient.invalidateQueries({ queryKey: ['artifacts'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteArtifact(artifact.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'snapshots'] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      setDeleteOpen(false);
      navigate('/artifacts');
    },
    onError: err => {
      setDeleteError(err.message);
    },
  });

  const supersedeMutation = useMutation({
    mutationFn: (oldArtifactId: string) => supersedeArtifact(artifact.id, oldArtifactId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'links'] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      void queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      setSupersedeOpen(false);
    },
    onError: err => {
      setSupersedeError(err.message);
    },
  });

  // Status transitions exposed by this panel.
  // Slice 2's `changeStatus` rejects every transition into `approved` (use the
  // snapshot approve flow). It also rejects transitions into / out of
  // `archived` (use the archive group). Notes/ADRs cannot enter `review`.
  function statusTransitions(): ArtifactStatus[] {
    const t = artifact.type;
    const noReview = t === 'adr' || t === 'note';
    if (artifact.status === 'draft' && !noReview) return ['review'];
    if (artifact.status === 'review') return ['draft'];
    return [];
  }

  function handleStatusChange(next: ArtifactStatus): void {
    setStatusError(null);
    patchMutation.mutate(
      { status: next },
      {
        onError: err => {
          setStatusError(err.message);
        },
      }
    );
  }

  function handleVisibilityFlip(): void {
    setVisibilityError(null);
    const next: ArtifactVisibility = artifact.visibility === 'team' ? 'private' : 'team';
    patchMutation.mutate(
      { visibility: next },
      {
        onError: err => {
          setVisibilityError(err.message);
        },
      }
    );
  }

  function handleArchive(): void {
    setArchiveError(null);
    patchMutation.mutate(
      { archived: true },
      {
        onError: err => {
          setArchiveError(err.message);
        },
      }
    );
  }

  function handleUnarchive(): void {
    setArchiveError(null);
    patchMutation.mutate(
      { archived: false, restoreStatus },
      {
        onError: err => {
          setArchiveError(err.message);
        },
      }
    );
  }

  const transitions = statusTransitions();
  const archived = artifact.status === 'archived';
  const restoreOptions = restoreTargetsForType(artifact.type);

  useEffect(() => {
    if (!restoreOptions.includes(restoreStatus)) {
      setRestoreStatus('draft');
    }
  }, [artifact.type, restoreOptions, restoreStatus]);

  return (
    <div className="flex flex-col gap-4 text-sm">
      <div className="flex flex-col gap-1">
        <span className="text-text-secondary">Status</span>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-text-primary">{artifact.status}</span>
          {!archived &&
            transitions.map(t => (
              <Button
                key={t}
                variant="outline"
                size="sm"
                disabled={!isOwnerOrAdmin || patchMutation.isPending}
                onClick={(): void => {
                  handleStatusChange(t);
                }}
              >
                Move to {t}
              </Button>
            ))}
          {!archived && transitions.length === 0 && (
            <span className="text-xs text-text-secondary">
              {artifact.status === 'approved'
                ? 'Approved status is set via snapshot approval.'
                : artifact.status === 'superseded'
                  ? 'Superseded status is set via the supersede action (Slice 5).'
                  : 'No status transitions available from here.'}
            </span>
          )}
        </div>
        {statusError && (
          <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
            {statusError}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-text-secondary">Visibility</span>
        <div className="flex items-center gap-2">
          <span className="font-mono text-text-primary">{artifact.visibility}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={!isOwnerOrAdmin || patchMutation.isPending}
            onClick={handleVisibilityFlip}
            className="inline-flex items-center gap-1.5"
          >
            {artifact.visibility === 'team' ? (
              <>
                <Lock className="size-4" />
                Make private
              </>
            ) : (
              <>
                <LockOpen className="size-4" />
                Make team
              </>
            )}
          </Button>
        </div>
        {visibilityError && (
          <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
            {visibilityError}
          </div>
        )}
      </div>

      <OwnerTransferControl artifact={artifact} />

      <div className="flex flex-col gap-1">
        <span className="text-text-secondary">Archive</span>
        {archived ? (
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={restoreStatus}
              onChange={e => {
                setRestoreStatus(e.target.value as UnarchiveRestoreStatus);
              }}
              className="rounded-md border border-border bg-surface px-2 py-1 text-sm"
              aria-label="Restore status"
            >
              {restoreOptions.map(s => (
                <option key={s} value={s}>
                  Restore as {s}
                </option>
              ))}
            </select>
            <Button
              variant="outline"
              size="sm"
              disabled={!isOwnerOrAdmin || patchMutation.isPending}
              onClick={handleUnarchive}
              className="inline-flex items-center gap-1.5"
            >
              <ArchiveRestore className="size-4" />
              Unarchive
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={!isOwnerOrAdmin || patchMutation.isPending}
            onClick={handleArchive}
            className="inline-flex w-fit items-center gap-1.5"
          >
            <Archive className="size-4" />
            Archive artifact
          </Button>
        )}
        {archiveError && (
          <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
            {archiveError}
          </div>
        )}
      </div>

      {artifact.type === 'adr' && (
        <div className="flex flex-col gap-1 border-t border-border pt-3">
          <span className="text-text-secondary">Supersession</span>
          <Button
            variant="outline"
            size="sm"
            disabled={!isOwnerOrAdmin}
            onClick={(): void => {
              setSupersedeError(null);
              setSupersedeOpen(true);
            }}
            className="inline-flex w-fit items-center gap-1.5"
          >
            <ArrowUpRightSquare className="size-4" />
            Supersede an older ADR
          </Button>
          <span className="text-xs text-text-secondary">
            Pick an older approved ADR. That ADR's status becomes "superseded" and a "supersedes"
            link is recorded from this ADR to it.
          </span>
          <LinkedArtifactPicker
            open={supersedeOpen}
            onOpenChange={setSupersedeOpen}
            codebaseId={artifact.codebaseId}
            excludeArtifactId={artifact.id}
            filter={{ type: 'adr', status: 'approved' }}
            title="Pick the older ADR to supersede"
            description="Only approved ADRs in this codebase are listed. The selected ADR will be marked superseded."
            confirmText="Supersede"
            onPick={(oldId): void => {
              supersedeMutation.mutate(oldId);
            }}
            pending={supersedeMutation.isPending}
            errorMessage={supersedeError}
          />
        </div>
      )}

      <div className="flex flex-col gap-1 border-t border-border pt-3">
        <span className="text-text-secondary">Danger zone</span>
        <Button
          variant="outline"
          size="sm"
          disabled={!isOwnerOrAdmin}
          onClick={(): void => {
            setDeleteError(null);
            setDeleteOpen(true);
          }}
          className="inline-flex w-fit items-center gap-1.5 border-error/50 text-error hover:bg-error/15"
        >
          <Trash2 className="size-4" />
          Delete artifact
        </Button>
        <span className="text-xs text-text-secondary">
          Soft delete. The slug stays reserved so old links cannot be reused.
        </span>
      </div>

      <ConfirmActionDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this artifact?"
        description="The artifact will be soft-deleted. Snapshots stay in the database. The slug remains reserved."
        confirmText="Delete artifact"
        confirmTone="danger"
        typeToConfirm={artifact.slug}
        onConfirm={(): void => {
          deleteMutation.mutate();
        }}
        pending={deleteMutation.isPending}
        errorMessage={deleteError}
      />
    </div>
  );
}
